import { Link, NavLink, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Activity, LayoutDashboard, History, Calculator, BookOpen, Package, Repeat, LogOut, UserCog, Shield, ChevronDown, Scale, Settings, Users, MessagesSquare, MessageSquare, BookOpenCheck, Keyboard, GraduationCap, HelpCircle, Utensils, Pill, Heart, Sun, Moon, Monitor, MessageCircleHeart, LineChart, UsersRound } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { useTheme } from "../context/ThemeContext";
import FeedbackDialog from "./FeedbackDialog";
import useKeyboardShortcuts from "../hooks/useKeyboardShortcuts";
import ShortcutHelpDialog from "./ShortcutHelpDialog";
import FloatingLogButton from "./FloatingLogButton";
import MobileBottomNav from "./MobileBottomNav";
import CommandPalette from "./CommandPalette";
import TimezoneChip from "./TimezoneChip";
import CoachMessageNotifier from "./CoachMessageNotifier";
import TermsReconsentModal from "./TermsReconsentModal";
import MobileNavDrawer from "./MobileNavDrawer";
import AnnouncementBanner from "./AnnouncementBanner";
import MuteBanner from "./MuteBanner";

const dashboardMenu = [
  { to: "/calculators", label: "Calculators", icon: Calculator, testid: "nav-calculators", flag: "calculators" },
  { to: "/library", label: "Library", icon: BookOpen, testid: "nav-library", flag: "library" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory", flag: "inventory" },
  { to: "/cycles", label: "Cycles", icon: Repeat, testid: "nav-cycles", flag: "cycles" },
  { to: "/weight", label: "Weight", icon: Scale, testid: "nav-weight", flag: "weight" },
  { to: "/nutrition", label: "Nutrition", icon: Utensils, testid: "nav-nutrition", flag: "nutrition" },
  { to: "/supplements", label: "Supplements", icon: Pill, testid: "nav-supplements", flag: "supplements" },
  { to: "/clients", label: "My Clients", icon: Users, testid: "nav-clients", coachOnly: true, flag: "coaching" },
  { to: "/history", label: "History", icon: History, testid: "nav-history", flag: "history" },
  { to: "/insights/timeline", label: "Timeline", icon: LineChart, testid: "nav-timeline" },
  { to: "/feed", label: "Feed", icon: Users, testid: "nav-feed", flag: "feed" },
  { to: "/tutorial", label: "Tutorial", icon: GraduationCap, testid: "nav-tutorial", flag: "tutorial" },
];

const primaryNav = [
  { to: "/community", label: "Community", icon: MessagesSquare, testid: "nav-community", flag: "community" },
  { to: "/coaches", label: "Coaches", icon: Users, testid: "nav-coaches", flag: "coaching" },
  { to: "/groups", label: "Groups", icon: UsersRound, testid: "nav-groups", flag: "coaching" },
  { to: "/playbook", label: "Playbook", icon: BookOpenCheck, testid: "nav-playbook", flag: "playbook" },
  { to: "/support", label: "Support Us", icon: Heart, testid: "nav-support" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { pref: themePref, resolved: themeResolved, cycleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = settings?.primary_color || "#0D9488";
  const flags = settings?.feature_flags || {};
  const isAdmin = user?.role === "admin";
  const [isCoachRaw, setIsCoachRaw] = useState(false);
  const isCoach = isCoachRaw || isAdmin; // Admins get full coach-tab visibility for support
  const isEnabled = (item) => (!item.flag || flags[item.flag] !== false) && (!item.coachOnly || isCoach);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchStatus = () => {
      api.get("/coach/status")
        .then(({ data }) => { if (!cancelled) setIsCoachRaw(!!data.is_coach); })
        .catch(() => { if (!cancelled) setIsCoachRaw(false); });
    };
    fetchStatus();
    // Refresh whenever the tab regains focus or becomes visible again — an admin
    // may have just toggled this user to coach in another tab. Cheap probe.
    const onFocus = () => fetchStatus();
    const onVisibility = () => { if (document.visibilityState === "visible") fetchStatus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user?.id]);
  const visibleDashboardMenu = dashboardMenu.filter(isEnabled);
  const visiblePrimaryNav = primaryNav.filter(isEnabled);
  const [pendingUsers, setPendingUsers] = useState(0);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);
  const dashboardActive = location.pathname === "/dashboard"
    || visibleDashboardMenu.some((m) => location.pathname === m.to || location.pathname.startsWith(m.to + "/"));

  const openShortcutHelp = useCallback(() => setShortcutHelpOpen(true), []);
  useKeyboardShortcuts({ isAdmin: user?.role === "admin", onHelpOpen: openShortcutHelp });

  const isStaff = user?.role === "admin" || user?.role === "moderator";

  useEffect(() => {
    if (!isStaff) { setPendingUsers(0); return; }
    let cancelled = false;
    const fetchCount = () => {
      api.get("/admin/users/pending_count")
        .then((r) => { if (!cancelled) setPendingUsers(r.data?.pending || 0); })
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isStaff]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Admin-only banner surfaced when maintenance is active. */}
      {user?.role === "admin" && settings?.maintenance_enabled && (
        <div
          className="bg-[#0D9488] text-white px-6 lg:px-10 py-2 flex flex-wrap items-center justify-between gap-3 font-mono-num text-[11px] uppercase tracking-widest"
          data-testid="admin-maintenance-banner"
        >
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-white animate-pulse" />
            <span className="font-black">// MAINTENANCE MODE ACTIVE</span>
            <span className="hidden sm:inline text-white/80">— non-admin users are seeing the maintenance page.</span>
          </div>
          <Link
            to="/admin/settings"
            className="border border-white hover:bg-white hover:text-[#0D9488] px-2 py-1 transition-colors"
            data-testid="admin-maintenance-banner-link"
          >
            Manage →
          </Link>
        </div>
      )}
      {/* Top bar: brand + user menu.
          Mobile: logo centered + hamburger on the right.
          Desktop: unchanged full horizontal layout. */}
      <header className="border-b border-slate-200 bg-white/85 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10 h-16 flex items-center gap-3">
          {/* Mobile-only invisible spacer so the brand truly centers */}
          <div className="w-10 md:hidden shrink-0" aria-hidden />

          <Link
            to="/dashboard"
            className="flex items-center gap-3 group flex-1 justify-center md:justify-start md:flex-none"
            data-testid="brand-logo"
          >
            {settings?.logo_url ? (
              <div className="w-9 h-9 border border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50">
                <img src={settings.logo_url} alt={settings.site_name} className="w-full h-full object-contain" data-testid="brand-logo-img" />
              </div>
            ) : (
              <div className="w-9 h-9 border border-slate-300 flex items-center justify-center" style={{ backgroundColor: `${brand}1a` }}>
                <Activity className="w-4 h-4" style={{ color: brand }} strokeWidth={2.5} />
              </div>
            )}
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display font-black text-lg tracking-tight uppercase" data-testid="brand-name">{settings?.site_name || "The Amino Exchange"}</span>
              {settings?.tagline && (
                <span className="font-mono-num text-[10px] text-slate-500 uppercase tracking-[0.25em] mt-0.5" data-testid="brand-tagline">{settings.tagline}</span>
              )}
            </div>
          </Link>

          {/* Desktop controls — hidden on mobile */}
          <div className="hidden md:flex items-center gap-3 ml-auto">
            <TimezoneChip />
            <button
              type="button"
              onClick={() => {
                // Route the tour trigger to the current page's tour where one exists;
                // fall back to the Dashboard tour otherwise.
                const p = location.pathname;
                const PAGES_WITH_TOURS = ["/profile", "/cycles", "/weight", "/calculator", "/calculators", "/inventory"];
                const target = PAGES_WITH_TOURS.includes(p) ? p : "/dashboard";
                navigate(`${target}?tour=1`);
              }}
              title="Take the tour"
              aria-label="Take the tour"
              data-testid="global-help-tour-trigger"
              className="w-9 h-9 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={openShortcutHelp}
              title="Keyboard shortcuts (press ?)"
              aria-label="Show keyboard shortcuts"
              data-testid="shortcut-help-trigger"
              className="hidden sm:flex w-9 h-9 items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors"
            >
              <Keyboard className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={cycleTheme}
              title={`Theme: ${themePref === "system" ? `System (${themeResolved})` : themePref}. Click to cycle.`}
              aria-label="Cycle theme: light, dark, system"
              data-testid="theme-toggle"
              className="w-9 h-9 flex items-center justify-center border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-500 transition-colors"
            >
              {themePref === "light" ? <Sun className="w-4 h-4" /> : themePref === "dark" ? <Moon className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
            </button>
            <NotificationBell brand={brand} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-50 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 border flex items-center justify-center font-display font-black text-sm" style={{ borderColor: `${brand}66`, backgroundColor: `${brand}1a`, color: brand }}>
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="hidden sm:flex flex-col items-start leading-tight pr-1">
                    <span className="text-xs text-slate-900 font-medium" data-testid="user-name">{user?.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{user?.role}</span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border border-slate-200 rounded-none w-60 text-slate-900 p-0" data-testid="user-menu">
                <DropdownMenuLabel className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 pt-3">// Account</DropdownMenuLabel>
                <div className="px-2 pb-2 text-xs text-slate-800 break-all">{user?.email}</div>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                  <Link to="/profile" data-testid="menu-profile" className="w-full flex items-center px-3 py-2">
                    <UserCog className="w-4 h-4 mr-2 text-slate-600" />
                    <span>Profile & Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                  <Link to="/messages" data-testid="menu-messages" className="w-full flex items-center px-3 py-2">
                    <MessageSquare className="w-4 h-4 mr-2 text-slate-600" />
                    <span>Messages</span>
                  </Link>
                </DropdownMenuItem>
                {isStaff && (
                  <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                    <Link to="/admin/users" data-testid="menu-admin-users" className="w-full flex items-center px-3 py-2 justify-between">
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>{user?.role === "moderator" ? "Moderation · Users" : "Users"}</span>
                      </span>
                      {pendingUsers > 0 && (
                        <span
                          className="font-mono-num text-[10px] px-1.5 py-0.5 uppercase tracking-widest"
                          style={{ background: brand, color: "#0a0a0b" }}
                          data-testid="admin-users-badge"
                        >
                          {pendingUsers} new
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                )}
                {isStaff && (
                  <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                    <Link to="/admin/supporters" data-testid="menu-admin-supporters" className="w-full flex items-center px-3 py-2">
                      <Heart className="w-4 h-4 mr-2" style={{ color: brand }} />
                      <span>Supporters</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <>
                    <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                      <Link to="/library" data-testid="menu-admin-library" className="w-full flex items-center px-3 py-2">
                        <Shield className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>Manage Library</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                      <Link to="/admin/theme" data-testid="menu-admin-theme" className="w-full flex items-center px-3 py-2">
                        <Settings className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>Theme Studio</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                      <Link to="/admin/settings" data-testid="menu-admin-settings" className="w-full flex items-center px-3 py-2">
                        <Settings className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>Site Settings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                      <Link to="/admin/security" data-testid="menu-admin-security" className="w-full flex items-center px-3 py-2">
                        <Settings className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>Security Audit</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                      <Link to="/admin/groups" data-testid="menu-admin-groups" className="w-full flex items-center px-3 py-2">
                        <UsersRound className="w-4 h-4 mr-2" style={{ color: brand }} />
                        <span>All Groups</span>
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setFeedbackOpen(true); }}
                  data-testid="menu-send-feedback"
                  className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer px-3 py-2"
                >
                  <MessageCircleHeart className="w-4 h-4 mr-2" style={{ color: brand }} />
                  <span>Send Feedback</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onSelect={handleLogout} data-testid="menu-logout" className="rounded-none cursor-pointer text-[#FF3B30] focus:text-[#FF3B30] focus:bg-[#FF3B30]/10 px-3 py-2">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile-only hamburger — opens slide-out drawer with every nav link */}
          <div className="md:hidden ml-auto">
            <MobileNavDrawer
              user={user}
              settings={settings}
              dashboardMenu={visibleDashboardMenu}
              primaryNav={visiblePrimaryNav}
              isStaff={isStaff}
              pendingUsers={pendingUsers}
              onLogout={handleLogout}
              onSendFeedback={() => setFeedbackOpen(true)}
            />
          </div>
        </div>
      </header>

      {/* Secondary nav bar — desktop only. On mobile the hamburger drawer covers everything. */}
      <nav className="hidden md:block border-b border-slate-200 bg-white/85 backdrop-blur-sm sticky top-16 z-20" data-testid="secondary-nav">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-12 flex items-center gap-1 overflow-x-auto">
          {/* Dashboard: link + submenu */}
          <DropdownMenu>
            <div
              className={`flex items-stretch border uppercase tracking-widest whitespace-nowrap ${
                dashboardActive
                  ? "border-slate-300 bg-slate-50 text-[#0D9488]"
                  : "border-transparent text-slate-600 hover:border-slate-200"
              }`}
              style={dashboardActive ? { borderBottomColor: brand } : {}}
            >
              <NavLink
                to="/dashboard"
                data-testid="nav-dashboard"
                className={({ isActive }) =>
                  `px-3 py-2 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                    isActive ? "text-[#0D9488]" : "text-inherit hover:text-slate-900"
                  }`
                }
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                Dashboard
              </NavLink>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="nav-dashboard-submenu"
                  className="px-2 border-l border-slate-200 text-slate-600 hover:text-slate-900 transition-colors flex items-center"
                  aria-label="Open dashboard submenu"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
            </div>
            <DropdownMenuContent align="start" className="bg-white border border-slate-200 rounded-none w-52 text-slate-900 p-0 mt-1" data-testid="nav-dashboard-menu">
              <DropdownMenuLabel className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 pt-3 pb-1">// Tools</DropdownMenuLabel>
              {visibleDashboardMenu.map((item) => (
                <DropdownMenuItem asChild key={item.to} className="rounded-none focus:bg-slate-50 focus:text-slate-900 cursor-pointer">
                  <NavLink
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `w-full flex items-center px-3 py-2 text-xs uppercase tracking-widest ${
                        isActive ? "bg-slate-50 text-[#0D9488]" : "text-slate-800"
                      }`
                    }
                  >
                    <item.icon className="w-3.5 h-3.5 mr-2 text-slate-500" />
                    <span>{item.label}</span>
                  </NavLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sibling top-level items */}
          {visiblePrimaryNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1.5 border uppercase tracking-widest whitespace-nowrap ${
                  isActive
                    ? "border-slate-300 bg-slate-50 text-[#0D9488]"
                    : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-200"
                }`
              }
              style={({ isActive }) => (isActive ? { borderBottomColor: brand } : {})}
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <AnnouncementBanner />
      <MuteBanner />

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 relative z-10 pb-24 md:pb-10">
        {children}
      </main>

      <footer className="border-t border-slate-200 bg-white/60 relative z-10" data-testid="app-footer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500">
          <div className="uppercase tracking-widest font-mono-num">
            © {new Date().getFullYear()} {settings?.site_name || "The Amino Exchange"} · Tracking tool, not medical advice
          </div>
          <div className="flex items-center gap-3 uppercase tracking-widest font-mono-num">
            <Link to="/privacy" className="hover:text-teal-700 font-semibold" data-testid="app-footer-privacy">Privacy</Link>
            <span className="text-slate-300">·</span>
            <Link to="/terms" className="hover:text-teal-700 font-semibold" data-testid="app-footer-terms">Terms</Link>
          </div>
        </div>
      </footer>

      <FloatingLogButton />
      {isCoach && (flags.coaching ?? true) !== false && <CoachMessageNotifier />}
      <CommandPalette isAdmin={user?.role === "admin"} isStaff={isStaff} />
      <TermsReconsentModal />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      <ShortcutHelpDialog
        open={shortcutHelpOpen}
        onOpenChange={setShortcutHelpOpen}
        isAdmin={user?.role === "admin"}
      />
    </div>
  );
}
